import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import {
  Sparkles, ChevronLeft, ChevronRight, Check, X, Send, Loader2,
  CalendarDays, Clock, Youtube, Instagram, Facebook, Music2, AlertTriangle
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const P_ICON = { youtube: Youtube, youtube_shorts: Youtube, instagram: Instagram, facebook: Facebook, tiktok: Music2 };
const STATUS_STYLE = {
  scheduled: 'bg-blue-500/15 text-blue-300 border-blue-500/30',
  awaiting_approval: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  publishing: 'bg-purple-500/15 text-purple-300 border-purple-500/30',
  published: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  completed: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  failed: 'bg-red-500/15 text-red-300 border-red-500/30',
  cancelled: 'bg-zinc-700/40 text-zinc-400 border-zinc-600/40',
  draft: 'bg-zinc-700/40 text-zinc-300 border-zinc-600/40',
};

function dayKey(d) { return d.toISOString().slice(0, 10); }

export const ContentCalendarView = ({ token, isFounder, onChanged }) => {
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [items, setItems] = useState([]);
  const [scope, setScope] = useState('user');
  const [view, setView] = useState('week');
  const [anchor, setAnchor] = useState(new Date());
  const [platformFilter, setPlatformFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [busy, setBusy] = useState(null);
  const [genOpen, setGenOpen] = useState(false);
  const [genGoal, setGenGoal] = useState('');
  const [genDays, setGenDays] = useState(7);
  const [genBusy, setGenBusy] = useState(false);
  const [proposal, setProposal] = useState(null);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/scheduler/?limit=200&account_scope=${scope}`, { headers });
      setItems(r.data.items || []);
    } catch { /* transient */ }
  }, [headers, scope]);

  useEffect(() => { load(); }, [load]);

  const act = async (id, action) => {
    setBusy(id + action);
    try {
      const r = await axios.post(`${API}/api/scheduler/${id}/${action}?account_scope=${scope}`, {}, { headers });
      if (action === 'publish_now') {
        if (r.data.success) toast.success('Published!');
        else toast.error(r.data.error || 'Publishing failed');
      } else {
        toast.success(action === 'approve' ? 'Approved — will publish on schedule' : 'Cancelled');
      }
      setSelected(null); load(); onChanged && onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally { setBusy(null); }
  };

  const generate = async () => {
    if (!genGoal.trim()) { toast.error('Tell Delphi your goal first'); return; }
    setGenBusy(true); setProposal(null);
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
      const r = await axios.post(`${API}/api/scheduler/generate-calendar?account_scope=${scope}`,
        { goal: genGoal, days: genDays, timezone: tz }, { headers });
      const d = r.data;
      if (d.mode === 'advisory') {
        setProposal(d);
        toast.info('Advisory mode — review the proposed calendar below');
      } else {
        toast.success(`Calendar created — ${d.posts} posts${d.status === 'awaiting_approval' ? ' awaiting your approval' : ' scheduled'}`);
        setGenOpen(false); setGenGoal(''); load(); onChanged && onChanged();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Calendar generation failed');
    } finally { setGenBusy(false); }
  };

  // Build visible day range
  const days = useMemo(() => {
    const list = [];
    const start = new Date(anchor);
    if (view === 'week') {
      start.setDate(start.getDate() - start.getDay());
      for (let i = 0; i < 7; i++) { const d = new Date(start); d.setDate(start.getDate() + i); list.push(d); }
    } else {
      start.setDate(1);
      const first = new Date(start); first.setDate(1 - first.getDay());
      for (let i = 0; i < 42; i++) { const d = new Date(first); d.setDate(first.getDate() + i); list.push(d); }
    }
    return list;
  }, [anchor, view]);

  const filtered = items.filter(i =>
    (platformFilter === 'all' || i.platform === platformFilter) &&
    (statusFilter === 'all' || i.status === statusFilter));
  const byDay = useMemo(() => {
    const m = {};
    filtered.forEach(i => {
      const k = (i.scheduled_time || '').slice(0, 10);
      (m[k] = m[k] || []).push(i);
    });
    return m;
  }, [filtered]);

  const shift = (dir) => {
    const d = new Date(anchor);
    if (view === 'week') d.setDate(d.getDate() + dir * 7); else d.setMonth(d.getMonth() + dir);
    setAnchor(d);
  };

  const todayKey = dayKey(new Date());
  const monthLabel = anchor.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });

  return (
    <div className="rounded-2xl border border-zinc-800 bg-zinc-900/50 p-4 mb-6" data-testid="content-calendar">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <CalendarDays className="w-5 h-5 text-orange-400" />
        <h2 className="text-base font-bold text-white mr-2">Content Calendar</h2>
        <div className="flex items-center gap-1">
          <Button size="sm" variant="ghost" onClick={() => shift(-1)} data-testid="calendar-prev"><ChevronLeft className="w-4 h-4" /></Button>
          <span className="text-sm text-zinc-300 min-w-[130px] text-center">{monthLabel}</span>
          <Button size="sm" variant="ghost" onClick={() => shift(1)} data-testid="calendar-next"><ChevronRight className="w-4 h-4" /></Button>
        </div>
        <div className="flex rounded-lg overflow-hidden border border-zinc-700">
          {['week', 'month'].map(v => (
            <button key={v} onClick={() => setView(v)} data-testid={`calendar-view-${v}`}
              className={`px-3 py-1 text-xs ${view === v ? 'bg-orange-600 text-white' : 'bg-zinc-800 text-zinc-400'}`}>
              {v[0].toUpperCase() + v.slice(1)}
            </button>
          ))}
        </div>
        <select value={platformFilter} onChange={e => setPlatformFilter(e.target.value)} data-testid="calendar-platform-filter"
          className="bg-zinc-800 border border-zinc-700 text-zinc-300 text-xs rounded-lg px-2 py-1.5">
          <option value="all">All platforms</option>
          {['instagram', 'youtube_shorts', 'youtube', 'tiktok', 'facebook'].map(p => <option key={p} value={p}>{p.replace('_', ' ')}</option>)}
        </select>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-testid="calendar-status-filter"
          className="bg-zinc-800 border border-zinc-700 text-zinc-300 text-xs rounded-lg px-2 py-1.5">
          <option value="all">All statuses</option>
          {['scheduled', 'awaiting_approval', 'published', 'failed', 'cancelled'].map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
        </select>
        {isFounder && (
          <select value={scope} onChange={e => setScope(e.target.value)} data-testid="calendar-scope-select"
            className="bg-zinc-800 border border-orange-500/40 text-orange-300 text-xs rounded-lg px-2 py-1.5">
            <option value="user">My calendar</option>
            <option value="system">Delphi AI (system org)</option>
          </select>
        )}
        <div className="flex-1" />
        <Button size="sm" onClick={() => setGenOpen(true)} data-testid="calendar-generate-btn"
          className="bg-orange-600 hover:bg-orange-500 text-white">
          <Sparkles className="w-4 h-4 mr-1" /> Generate with AI
        </Button>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-7 gap-1">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
          <div key={d} className="text-[10px] uppercase tracking-wider text-zinc-500 text-center py-1">{d}</div>
        ))}
        {days.map((d, idx) => {
          const k = dayKey(d);
          const dayItems = byDay[k] || [];
          const isToday = k === todayKey;
          const inMonth = d.getMonth() === anchor.getMonth();
          return (
            <div key={idx} data-testid={`calendar-day-${k}`}
              className={`rounded-lg border p-1 ${view === 'week' ? 'min-h-[110px]' : 'min-h-[72px]'}
                ${isToday ? 'border-orange-500/60 bg-orange-500/5' : 'border-zinc-800 bg-zinc-900/40'}
                ${!inMonth && view === 'month' ? 'opacity-40' : ''}`}>
              <div className={`text-[10px] mb-1 ${isToday ? 'text-orange-400 font-bold' : 'text-zinc-500'}`}>{d.getDate()}</div>
              <div className="space-y-1">
                {dayItems.slice(0, view === 'week' ? 4 : 2).map(item => {
                  const Icon = P_ICON[item.platform] || CalendarDays;
                  return (
                    <button key={item.id} onClick={() => setSelected(item)} data-testid={`calendar-item-${item.id}`}
                      className={`w-full text-left px-1.5 py-1 rounded border text-[10px] leading-tight truncate flex items-center gap-1 ${STATUS_STYLE[item.status] || STATUS_STYLE.draft}`}>
                      <Icon className="w-3 h-3 shrink-0" />
                      <span className="truncate">{item.title}</span>
                    </button>
                  );
                })}
                {dayItems.length > (view === 'week' ? 4 : 2) && (
                  <div className="text-[9px] text-zinc-500 pl-1">+{dayItems.length - (view === 'week' ? 4 : 2)} more</div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Item detail */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-md">
          {selected && (
            <>
              <DialogHeader><DialogTitle className="text-white">{selected.title}</DialogTitle></DialogHeader>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[11px] px-2 py-0.5 rounded-full border ${STATUS_STYLE[selected.status]}`} data-testid="calendar-item-status">
                    {selected.status.replace('_', ' ')}
                  </span>
                  <span className="text-zinc-400 text-xs flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {new Date(selected.scheduled_time).toLocaleString()}
                  </span>
                  <span className="text-zinc-400 text-xs capitalize">{selected.platform?.replace('_', ' ')}</span>
                </div>
                {selected.theme && <p className="text-xs text-orange-300">Theme: {selected.theme}</p>}
                {selected.caption && <p className="text-zinc-300 text-xs whitespace-pre-wrap max-h-32 overflow-y-auto">{selected.caption}</p>}
                {selected.asset_requirements && (
                  <p className="text-xs text-amber-300 flex items-start gap-1">
                    <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" /> Asset needed: {selected.asset_requirements}
                  </p>
                )}
                {selected.last_error && <p className="text-xs text-red-400">{selected.last_error}</p>}
                {selected.platform_url && (
                  <a href={selected.platform_url} target="_blank" rel="noreferrer" className="text-xs text-blue-400 underline">View live post</a>
                )}
              </div>
              <div className="flex gap-2 pt-2 flex-wrap">
                {selected.status === 'awaiting_approval' && (
                  <Button size="sm" onClick={() => act(selected.id, 'approve')} disabled={!!busy}
                    className="bg-emerald-600 hover:bg-emerald-500" data-testid="calendar-approve-btn">
                    <Check className="w-4 h-4 mr-1" /> Approve
                  </Button>
                )}
                {['scheduled', 'awaiting_approval', 'failed', 'draft'].includes(selected.status) && (
                  <>
                    <Button size="sm" onClick={() => act(selected.id, 'publish_now')} disabled={!!busy}
                      className="bg-orange-600 hover:bg-orange-500" data-testid="calendar-publish-now-btn">
                      {busy === selected.id + 'publish_now' ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1" /> Publish now</>}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => act(selected.id, 'reject')} disabled={!!busy}
                      className="border-zinc-700 text-zinc-400" data-testid="calendar-cancel-btn">
                      <X className="w-4 h-4 mr-1" /> Cancel post
                    </Button>
                  </>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* AI generation dialog */}
      <Dialog open={genOpen} onOpenChange={setGenOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-orange-400" /> Let Delphi plan your calendar
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <input value={genGoal} onChange={e => setGenGoal(e.target.value)} data-testid="calendar-goal-input"
              placeholder='e.g. "Grow my fitness brand"'
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white placeholder-zinc-500" />
            <div className="flex items-center gap-2">
              <span className="text-xs text-zinc-400">Length:</span>
              {[7, 14, 30].map(d => (
                <button key={d} onClick={() => setGenDays(d)} data-testid={`calendar-days-${d}`}
                  className={`px-3 py-1 rounded-lg text-xs border ${genDays === d ? 'bg-orange-600 border-orange-500 text-white' : 'bg-zinc-800 border-zinc-700 text-zinc-400'}`}>
                  {d} days
                </button>
              ))}
            </div>
            <p className="text-[11px] text-zinc-500">
              Delphi's Strategy, Content and Social agents will plan cadence, themes and individual posts.
              Depending on your autonomy settings, the calendar is proposed, held for approval, or scheduled automatically.
            </p>
            <Button onClick={generate} disabled={genBusy} data-testid="calendar-generate-submit"
              className="w-full bg-orange-600 hover:bg-orange-500">
              {genBusy ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Delphi is planning…</> : 'Generate calendar'}
            </Button>
            {proposal && (
              <div className="max-h-56 overflow-y-auto space-y-1 border-t border-zinc-800 pt-2" data-testid="calendar-proposal">
                <p className="text-xs text-amber-300">Advisory mode — proposed calendar (not scheduled):</p>
                {(proposal.proposed || []).map((e, i) => (
                  <div key={i} className="text-[11px] text-zinc-300 flex gap-2">
                    <span className="text-zinc-500 shrink-0">{new Date(e.scheduled_time).toLocaleDateString()}</span>
                    <span className="capitalize text-zinc-400 shrink-0">{e.platform.replace('_', ' ')}</span>
                    <span className="truncate">{e.title}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
